import type { ConditionalValue } from '@chakra-ui/react'

export interface StyleThemingProps {
  variant?: string
  size?: string | ConditionalValue<string>
  colorScheme?: string
  [key: string]: any
}

export type MultiStyleType<T extends readonly string[]> = Record<
  T[number],
  Record<string, any>
>

function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: Array<Partial<T> | undefined>
): T {
  if (!sources.length) return target
  const source = sources.shift()

  if (source === undefined) {
    return deepMerge(target, ...sources)
  }

  for (const key in source) {
    const sourceValue = source[key]
    const targetValue = target[key]

    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      target[key] = deepMerge({ ...targetValue }, sourceValue as any)
    } else if (sourceValue !== undefined) {
      target[key] = sourceValue as any
    }
  }

  return deepMerge(target, ...sources)
}

interface MultiStyleConfig {
  baseStyle?: Record<string, any> | ((props?: any) => Record<string, any>)
  sizes?: Record<string, Record<string, any>>
  variants?: Record<string, Record<string, any>>
  defaultProps?: StyleThemingProps
}

function isResponsiveObject(value: any): value is Record<string, string> {
  return (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value.base !== undefined ||
      Object.keys(value).some((k) =>
        ['sm', 'md', 'lg', 'xl', '2xl'].includes(k),
      ))
  )
}

function mergeResponsiveStyles(
  baseStyles: Record<string, any>,
  responsiveSizeOrVariant: Record<string, string>,
  configMap: Record<string, Record<string, any>>,
): Record<string, any> {
  const result = { ...baseStyles }

  for (const part in result) {
    const partResult: Record<string, any> = { ...result[part] }

    for (const breakpoint in responsiveSizeOrVariant) {
      const configKey = responsiveSizeOrVariant[breakpoint]
      const configStyles = configMap[configKey]

      if (configStyles && configStyles[part]) {
        for (const prop in configStyles[part]) {
          const value = configStyles[part][prop]

          if (breakpoint === 'base') {
            partResult[prop] = value
          } else {
            if (
              typeof partResult[prop] === 'object' &&
              !Array.isArray(partResult[prop])
            ) {
              partResult[prop] = { ...partResult[prop], [breakpoint]: value }
            } else {
              const baseValue = partResult[prop]
              partResult[prop] = { base: baseValue, [breakpoint]: value }
            }
          }
        }
      }
    }

    result[part] = partResult
  }

  return result
}

export function mergeMultiStyleConfig(
  styleConfig: MultiStyleConfig,
  props?: StyleThemingProps,
): Record<string, any> {
  const {
    baseStyle = {},
    sizes = {},
    variants = {},
    defaultProps = {},
  } = styleConfig
  const { size = defaultProps.size, variant = defaultProps.variant } =
    props || {}

  let mergedStyles =
    typeof baseStyle === 'function' ? { ...baseStyle(props) } : { ...baseStyle }

  if (size) {
    if (typeof size === 'string') {
      if (sizes[size]) {
        mergedStyles = deepMerge(mergedStyles, sizes[size])
      }
    } else if (isResponsiveObject(size)) {
      mergedStyles = mergeResponsiveStyles(mergedStyles, size, sizes)
    } else if (Array.isArray(size)) {
      const firstSize = size.find((v) => v != null)
      if (firstSize && sizes[firstSize]) {
        mergedStyles = deepMerge(mergedStyles, sizes[firstSize])
      }
    }
  }

  if (variant) {
    if (typeof variant === 'string') {
      if (variants[variant]) {
        mergedStyles = deepMerge(mergedStyles, variants[variant])
      }
    } else if (isResponsiveObject(variant)) {
      mergedStyles = mergeResponsiveStyles(mergedStyles, variant, variants)
    } else if (Array.isArray(variant)) {
      const variantArray = variant as string[]
      const firstVariant = variantArray.find((v) => v != null)
      if (firstVariant && variants[firstVariant]) {
        mergedStyles = deepMerge(mergedStyles, variants[firstVariant])
      }
    }
  }

  return mergedStyles
}

export function createMultiStyleConfigHelpers<T extends readonly string[]>(
  parts: T,
) {
  return {
    parts,
    defineMultiStyleConfig: <C>(config: C): C => config,
    definePartsStyle: <C>(config: C): C => config,
  }
}
